import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Phone, Clock, FileText, ChevronRight, X, Play, Sparkles, Activity } from 'lucide-react';
import { GlassCard, CardLabel, Pill } from './GlassCard';
import CallReplayPanel from './CallReplayPanel';
import { useLocations } from '../../context/LocationContext';

const fmtDuration = (s) => {
  const sec = s || 0;
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return `${m}m ${r}s`;
};

export default function CallsPage() {
  const { api } = useAuth();
  const { activeLocationId, apiQueryParam } = useLocations();
  const [calls, setCalls] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/calls${apiQueryParam()}`); setCalls(data.calls || []); } catch {}
    })();
  }, [api, activeLocationId, apiQueryParam]);

  const totalSec = calls.reduce((sum, c) => sum + (c.duration_seconds || c.duration_secs || 0), 0);

  return (
    <div data-testid="calls-page" className="space-y-6">
      <h1 className="font-heading text-2xl font-semibold text-gray-900 dark:text-white">Calls</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat icon={<Phone className="w-4 h-4" />} value={calls.length} label="Total Calls" />
        <Stat icon={<Clock className="w-4 h-4" />} value={fmtDuration(totalSec)} label="Total Duration" />
        <Stat icon={<FileText className="w-4 h-4" />} value={calls.filter(c => c.recording_url || c.transcript).length} label="With Recording" />
      </div>

      <div data-testid="calls-list" className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {calls.length === 0 ? (
          <GlassCard className="col-span-full text-center py-14">
            <p className="text-gray-400 dark:text-slate-500 text-sm">No calls yet. Your AI agent will log calls here automatically.</p>
          </GlassCard>
        ) : calls.map(call => {
          const phone = call.caller_phone || call.caller_number || 'Unknown';
          const tone = OUTCOME_TONE[call.outcome] || 'neutral';
          return (
            <GlassCard key={call.id} className="p-5 hover:border-blue-300 dark:hover:border-blue-500/30 transition-all cursor-pointer"
              onClick={() => setSelected(call)} data-testid={`call-row-${call.id}`}>
              <div className="flex items-center justify-between mb-4">
                <CardLabel>{new Date(call.created_at).toLocaleDateString()} · {fmtDuration(call.duration_seconds || call.duration_secs)}</CardLabel>
                <Pill tone={tone}>{(call.outcome || 'unknown').replace('_', ' ')}</Pill>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="relative w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-purple-700 shadow-[0_0_24px_rgba(59,130,246,0.45)] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold text-gray-900 dark:text-white tabular-nums truncate">{phone}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Ava picked up</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 dark:text-slate-600 flex-shrink-0" />
              </div>
              {(call.summary || call.ai_summary) && (
                <div className="flex items-start gap-3 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-[14px] px-3.5 py-2.5">
                  <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" />
                  <div className="min-w-0 flex-1">
                    <CardLabel>AI summary</CardLabel>
                    <p className="text-xs text-gray-700 dark:text-slate-300 mt-1 leading-relaxed line-clamp-2">{call.summary || call.ai_summary}</p>
                  </div>
                </div>
              )}
            </GlassCard>
          );
        })}
      </div>

      {selected && <CallDrawer call={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Stat({ icon, value, label }) {
  return (
    <GlassCard className="p-5">
      <div className="flex items-center justify-between mb-3">
        <CardLabel>{label}</CardLabel>
        <span className="text-gray-400 dark:text-slate-500">{icon}</span>
      </div>
      <p className="text-2xl sm:text-3xl font-heading font-semibold text-gray-900 dark:text-white tabular-nums leading-none">{value}</p>
    </GlassCard>
  );
}

const OUTCOME_TONE = {
  booked: 'hot',
  lead_captured: 'blue',
  no_action: 'neutral',
  wrong_number: 'amber',
};

function OutcomeBadge({ outcome }) {
  const tone = OUTCOME_TONE[outcome] || 'neutral';
  const label = (outcome || 'unknown').replace('_', ' ');
  return <Pill tone={tone}>{label}</Pill>;
}

function CallDrawer({ call, onClose }) {
  const { api } = useAuth();
  const [showReplay, setShowReplay] = useState(false);
  const transcript = call.transcript || '';
  const callId = call.vapi_call_id || call.call_id || call.id;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div data-testid="call-drawer" className="relative ml-auto w-full max-w-xl h-full bg-white dark:bg-[#1a1a1c] border-l border-gray-200 dark:border-white/10 shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-[#1a1a1c] border-b border-gray-100 dark:border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">Call detail</p>
            <h3 className="font-heading text-lg font-semibold text-gray-900 dark:text-white">{call.caller_phone || 'Unknown caller'}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-white/[0.05] rounded-lg" data-testid="close-call-drawer">
            <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <Meta label="Date" value={new Date(call.created_at).toLocaleString()} />
            <Meta label="Duration" value={fmtDuration(call.duration_seconds || call.duration_secs)} />
            <Meta label="Outcome" value={<OutcomeBadge outcome={call.outcome} />} />
          </div>

          {call.summary && (
            <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/10 rounded-xl p-4">
              <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1 font-medium">AI Summary</p>
              <p className="text-sm text-gray-800 dark:text-slate-200 leading-relaxed">{call.summary}</p>
            </div>
          )}

          {call.recording_url ? (
            <div className="bg-blue-50 dark:bg-blue-500/15 border border-blue-100 dark:border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Play className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <p className="text-xs uppercase tracking-wide font-medium text-blue-700 dark:text-blue-300">Recording</p>
              </div>
              <audio controls className="w-full" src={call.recording_url} data-testid="call-recording-audio">
                Your browser does not support audio playback.
              </audio>
              <a href={call.recording_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-2 inline-block">Open in new tab</a>
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-white/[0.03] border border-dashed border-gray-200 dark:border-white/10 rounded-xl p-4 text-center">
              <p className="text-xs text-gray-400 dark:text-slate-500">No recording available</p>
            </div>
          )}

          <div>
            <p className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-slate-400 mb-2">Transcript</p>
            {transcript ? (
              <div data-testid="call-transcript" className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl p-4 max-h-96 overflow-y-auto">
                <pre className="text-xs text-gray-700 dark:text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">{transcript}</pre>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-white/[0.03] border border-dashed border-gray-200 dark:border-white/10 rounded-xl p-4 text-center">
                <p className="text-xs text-gray-400 dark:text-slate-500">Transcript not available yet, typically appears 1-2 minutes after call ends.</p>
              </div>
            )}
          </div>

          {/* Conversation replay timeline (Milestone D) */}
          <div>
            <button
              data-testid="call-replay-toggle"
              onClick={() => setShowReplay(s => !s)}
              className="w-full flex items-center justify-between bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/30 hover:border-purple-300 dark:hover:border-purple-500/60 rounded-xl px-4 py-3 transition-colors">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <p className="text-sm font-medium text-purple-700 dark:text-purple-200">Conversation replay</p>
              </div>
              <ChevronRight className={`w-4 h-4 text-purple-600 dark:text-purple-300 transition-transform ${showReplay ? 'rotate-90' : ''}`} />
            </button>
            {showReplay && (
              <div className="mt-3">
                <CallReplayPanel api={api} callId={callId} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wide font-medium mb-1">{label}</p>
      <div className="text-sm text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}
